/** YYYY-MM-DD in local calendar (avoids UTC shift on date-only strings). */

export function parseLeadFieldIsoDate(
  s: string | number | null | undefined
): Date | null {
  if (s == null || s === '') return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s).trim());
  if (!m) return null;
  const y = +m[1];
  const mo = +m[2] - 1;
  const d = +m[3];
  const dt = new Date(y, mo, d);
  return isNaN(dt.getTime()) ? null : dt;
}

export function formatLeadFieldIsoDate(d: Date | null): string {
  if (!d || !(d instanceof Date) || isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${mo}-${day}`;
}

/** Local calendar start of today (for date-only pickers). */
export function startOfTodayLocal(): Date {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
}

/**
 * Date lead fields may only be filled from today onward.
 * Optional configured min (stored in `min-length`) applies only if it is later than today.
 */
export function getEffectiveLeadFieldMinDate(
  configuredMinIso: string | number | null | undefined
): Date {
  const today = startOfTodayLocal();
  const configured = parseLeadFieldIsoDate(configuredMinIso);
  if (!configured) {
    return today;
  }
  configured.setHours(0, 0, 0, 0);
  return configured.getTime() > today.getTime() ? configured : today;
}

export function getEffectiveLeadFieldMinDateIso(
  configuredMinIso: string | number | null | undefined
): string {
  return formatLeadFieldIsoDate(getEffectiveLeadFieldMinDate(configuredMinIso));
}

export function isLeadFieldDateBeforeMin(
  valueIso: string | number | null | undefined,
  configuredMinIso: string | number | null | undefined
): boolean {
  const value = parseLeadFieldIsoDate(valueIso);
  if (!value) {
    return false;
  }
  value.setHours(0, 0, 0, 0);
  const min = getEffectiveLeadFieldMinDate(configuredMinIso);
  return value.getTime() < min.getTime();
}

function normalizeIso(raw: string | number | null | undefined): string {
  if (raw == null || raw === '') return '';
  return String(raw).trim();
}

/**
 * MatDatepicker + [ngModel]="parse(iso)" freezes if parse() returns a new Date every
 * change-detection pass. This cache returns the same Date reference until the ISO string changes.
 */
export class StableLeadFieldDateCache {
  private map = new Map<string, { iso: string; date: Date | null }>();

  getModel(cacheKey: string, rawIso: string | number | null | undefined): Date | null {
    const iso = normalizeIso(rawIso);
    const row = this.map.get(cacheKey);
    if (row !== undefined && row.iso === iso) {
      return row.date;
    }
    const date = iso === '' ? null : parseLeadFieldIsoDate(iso);
    this.map.set(cacheKey, { iso, date });
    return date;
  }

  rememberPick(cacheKey: string, d: Date | null): string {
    const iso = formatLeadFieldIsoDate(d) || '';
    this.map.set(cacheKey, { iso, date: d });
    return iso;
  }

  clear(): void {
    this.map.clear();
  }
}
